import { useMemo, useState } from "react";
import AddedArtifactList from "@/components/evidenceGathering/AddedArtifactList";
import ArtifactCatalogDialog from "@/components/evidenceGathering/ArtifactCatalogDialog";
import ArtifactPreviewDialog from "@/components/evidenceGathering/ArtifactPreviewDialog";
import ArtifactReviewDialog from "@/components/evidenceGathering/ArtifactReviewDialog";
import EmptyArtifactState from "@/components/evidenceGathering/EmptyArtifactState";
import UploadCemModel from "@/components/evidenceGathering/UploadCemModel";
import { ErrorBanner } from "@/components/ingestion";
import { Button } from "@/components/ui/button";
import { useArtifacts, useDeactivateArtifacts } from "@/hooks/useProjects";
import { latestUploadVersion } from "@/lib/artifacts";
import { recordArtefactEvents } from "@/lib/evidenceAudit";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactsPanelProps {
  projectId: string;
}

const ArtifactsPanel = ({ projectId }: ArtifactsPanelProps) => {
  const [artifactCatalogOpen, setArtifactCatalogOpen] = useState(false);
  // Set when a review follows straight on from an upload: which artefacts
  // came out, and the workbook they came from so a failed one can be retried.
  // Owned here rather than in UploadCemModel, which its own success unmounts
  // on a first upload — the arriving artefacts replace the empty state it
  // sits inside.
  const [extracted, setExtracted] = useState<{ uids: string[]; file: File } | null>(null);
  const [removingArtifactUids, setRemovingArtifactUids] = useState<Set<string>>(new Set());
  // The uid, not the artifact: holding the object would freeze a snapshot
  // taken when it was clicked, so a saved description would appear to revert
  // and a status change from polling would never reach the open dialog.
  const [previewUid, setPreviewUid] = useState<string | null>(null);
  const deactivateArtifacts = useDeactivateArtifacts();

  const {
    data: artifacts = [],
    isError: isArtifactsError,
    error: artifactsError,
  } = useArtifacts(projectId);
  const activeArtifacts = useMemo(() => artifacts.filter((a) => a.isActive), [artifacts]);
  // Over every artefact, not just the active ones: deactivating the newest
  // must not relight an older one as though it were current.
  const latestUpload = useMemo(() => latestUploadVersion(artifacts), [artifacts]);
  const previewArtifact = useMemo(
    () => activeArtifacts.find((a) => a.uid === previewUid) ?? null,
    [activeArtifacts, previewUid]
  );
  const hasActiveArtifacts = activeArtifacts.length > 0;
  const hasAnyArtifacts = artifacts.length > 0;

  const handleExtracted = (uids: string[], file: File) => setExtracted({ uids, file });

  // Derived on every render so the rows keep up with the polling query — the
  // descriptions arrive minutes after the uids do.
  const reviewArtifacts = extracted
    ? artifacts.filter((a) => extracted.uids.includes(a.uid))
    : [];

  const handleRemoveArtifact = (artifact: Artifact) => {
    setRemovingArtifactUids((prev) => new Set(prev).add(artifact.uid));
    // mutateAsync rather than per-call callbacks: those only fire for the latest of
    // overlapping calls, and removals here can overlap (one per card).
    deactivateArtifacts
      .mutateAsync({ projectId, uids: [artifact.uid] })
      .then(() => recordArtefactEvents(projectId, "remove_artefact", [artifact]))
      .catch(() => {
        // Surfaced via deactivateArtifacts.isError.
      })
      .finally(() =>
        setRemovingArtifactUids((prev) => {
          const next = new Set(prev);
          next.delete(artifact.uid);
          return next;
        })
      );
  };

  return (
    <>
      <div className={hasActiveArtifacts ? "flex items-start justify-between" : "flex flex-col items-center text-center"}>
        <div>
          <h2 className="text-lg font-bold text-foreground">Add Artefacts</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Upload your Cost-Effectiveness Model document to identify its artefacts.
          </p>
        </div>
        {hasActiveArtifacts && (
          <div className="flex items-center gap-2">
            <Button type="button" variant="brand" onClick={() => setArtifactCatalogOpen(true)}>
              Select from Extracted Artefacts
            </Button>
            <UploadCemModel
              projectId={projectId}
              triggerVariant="outline"
              triggerLabel="Upload CEM File"
              onExtracted={handleExtracted}
            />
          </div>
        )}
      </div>

      <ErrorBanner
        message={isArtifactsError ? (artifactsError?.message || "Failed to load artefacts. Please refresh and try again.") : null}
        className="mb-4"
      />
      <ErrorBanner
        message={deactivateArtifacts.isError ? (deactivateArtifacts.error?.message || "Failed to remove artefact. Please try again.") : null}
        className="mb-4"
      />

      {hasActiveArtifacts ? (
        <AddedArtifactList
          artifacts={activeArtifacts}
          projectId={projectId}
          onView={(artifact) => setPreviewUid(artifact.uid)}
          onRemove={handleRemoveArtifact}
          removingUids={removingArtifactUids}
          latestUploadVersion={latestUpload}
        />
      ) : (
        <EmptyArtifactState>
          {hasAnyArtifacts && (
            <>
              <Button type="button" variant="brand" onClick={() => setArtifactCatalogOpen(true)} className="mb-4">
                Select from Extracted Artefacts
              </Button>
              <div className="flex items-center gap-3 w-full mb-4 max-w-xs">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium text-muted-foreground">OR</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}
          <UploadCemModel
            projectId={projectId}
            presentation="dropzone"
            onExtracted={handleExtracted}
          />
        </EmptyArtifactState>
      )}

      <ArtifactPreviewDialog
        artifact={previewArtifact}
        projectId={projectId}
        onOpenChange={(open) => !open && setPreviewUid(null)}
        latestUploadVersion={latestUpload}
      />

      <ArtifactCatalogDialog
        open={artifactCatalogOpen}
        onOpenChange={setArtifactCatalogOpen}
        artifacts={artifacts}
        projectId={projectId}
      />

      {extracted && (
        <ArtifactReviewDialog
          open
          onOpenChange={(open) => !open && setExtracted(null)}
          artifacts={reviewArtifacts}
          projectId={projectId}
          latestUploadVersion={latestUpload}
          sourceFile={extracted.file}
        />
      )}
    </>
  );
};

export default ArtifactsPanel;
